<template>
	<div class="listing-form">
		<!-- Buy / Sell, for deals that trade at the market price (metals, NGX stocks). Selling waits
		     until you hold some. -->
		<div v-if="market" class="side-tabs" role="tablist" aria-label="Buy or sell">
			<button
				type="button"
				role="tab"
				class="side-tab"
				:class="{ active: !isSell }"
				:aria-selected="!isSell"
				@click="setSide('Buy')"
			>
				<svg viewBox="0 0 20 20" fill="none"><path d="M10 16V4M10 4L6 8M10 4L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
				Buy {{ subjectName }}
			</button>
			<button
				type="button"
				role="tab"
				class="side-tab"
				:class="{ active: isSell }"
				:aria-selected="isSell"
				:disabled="!canSell"
				:title="canSell ? '' : `You can sell once you hold some ${subjectName.toLowerCase()}`"
				@click="setSide('Sell')"
			>
				<svg viewBox="0 0 20 20" fill="none"><path d="M10 4V16M10 16L6 12M10 16L14 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
				Sell {{ subjectName }}
			</button>
		</div>

		<!-- Balance card: the company's cash to buy with, or what it holds to sell -->
		<div class="balance-card">
			<div class="balance-icon">
				<svg viewBox="0 0 24 24" fill="none">
					<path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
				</svg>
			</div>
			<div class="balance-info">
				<span class="balance-label">{{ isSell ? "You hold" : "Available Cash" }}</span>
				<span class="balance-value">{{ isSell ? heldLabel : formatAmountToDollar(balance, 2, currency) }}</span>
			</div>
		</div>

		<form class="invest-form" @submit.prevent="showModal">
			<!-- Buy: an amount of money, at the real price and against the real minimum -->
			<div v-if="!isSell" class="form-section">
				<div class="section-header">
					<h3 class="section-title">How much would you like to invest?</h3>
					<p class="section-subtitle">
						Price: <span class="price-highlight">{{ formatAmountToDollar(perUnit(buyPrice), buyPriceDecimals, currency) }}</span> {{ perUnitText }}
					</p>
				</div>

				<TradeNumberInput
					placeHolder="0.00"
					:label="`${currency} amount`"
					:amount="payload.currencyAmount"
					:minNumber="minInvestment"
					:maxNumber="balance"
					:currency="currency"
					:checkValidity="amountCheck"
					type="text"
					fieldType="amount"
					name="currencyAmount"
					id="currencyAmount"
					@set="setCurrencyAmount"
					@valid="currencyAmountValid"
					@validate="validate"
				/>

				<div class="trade-info">
					<div class="info-row">
						<span class="info-label">Minimum investment</span>
						<span class="info-value">{{ formatAmountToDollar(minInvestment, 2, currency) }}</span>
					</div>
					<div class="info-row">
						<span class="info-label">Transaction fee</span>
						<span class="info-value">{{ buyFeePercent }}%</span>
					</div>
				</div>
			</div>

			<!-- Sell: how much of what you hold, at today's price -->
			<div v-else class="form-section">
				<div class="section-header">
					<h3 class="section-title">{{ sellQuestion }}</h3>
					<p class="section-subtitle">
						Price: <span class="price-highlight">{{ formatAmountToDollar(perUnit(sellPrice), buyPriceDecimals, currency) }}</span> {{ perUnitText }}, today's price
					</p>
				</div>

				<TradeNumberInput
					placeHolder="0"
					:label="inputUnitLabel"
					:amount="sell.units"
					:minNumber="0.0001"
					:maxNumber="heldUnits"
					:checkValidity="amountCheck"
					type="text"
					fieldType="amount"
					name="sellUnits"
					id="sellUnits"
					@set="setSellUnits"
					@valid="sellUnitsValid"
					@validate="validate"
				/>
				<button type="button" class="sell-all" @click="setSellUnits(String(heldUnits))">Sell all {{ heldLabel }}</button>

				<div class="trade-info">
					<div class="info-row">
						<span class="info-label">Transaction fee</span>
						<span class="info-value">{{ sellFeePercent }}%</span>
					</div>
				</div>
			</div>

			<div v-if="isSell" class="summary-card">
				<div class="summary-row">
					<span class="summary-label">You sell</span>
					<span class="summary-value">{{ quantityLabel(sell.tokens) }}</span>
				</div>
				<div class="summary-row">
					<span class="summary-label">Sale amount</span>
					<span class="summary-value">{{ formatAmountToDollar(sellGross, 2, currency) }}</span>
				</div>
				<div class="summary-row">
					<span class="summary-label">Transaction fee</span>
					<span class="summary-value">{{ formatAmountToDollar(sellFeeAmount, 2, currency) }}</span>
				</div>
				<div class="summary-divider"></div>
				<div class="summary-row total">
					<span class="summary-label">You receive</span>
					<span class="summary-value">{{ formatAmountToDollar(sellNet, 2, currency) }}</span>
				</div>
			</div>

			<div v-else class="summary-card">
				<!-- What the money buys, so the price above never has to be divided in your head. -->
				<div class="summary-row">
					<span class="summary-label">You get</span>
					<span class="summary-value">{{ quantityLabel(payload.tokens) }}</span>
				</div>
				<div class="summary-row">
					<span class="summary-label">Investment amount</span>
					<span class="summary-value">{{ formatAmountToDollar(investmentAmount, 2, currency) }}</span>
				</div>
				<div class="summary-row">
					<span class="summary-label">Transaction fee</span>
					<span class="summary-value">{{ formatAmountToDollar(buyFeeAmount, 2, currency) }}</span>
				</div>
				<div class="summary-divider"></div>
				<div class="summary-row total">
					<span class="summary-label">Total you pay</span>
					<span class="summary-value">{{ formatAmountToDollar(totalPaymentAmount, 2, currency) }}</span>
				</div>
			</div>

			<button type="submit" class="submit-btn">
				<svg v-if="isSell" viewBox="0 0 20 20" fill="none">
					<path d="M10 4V16M10 16L6 12M10 16L14 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
				<svg v-else viewBox="0 0 20 20" fill="none">
					<path d="M10 16V4M10 4L6 8M10 4L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
				{{ submitLabel }}
			</button>
		</form>

		<!-- Confirmation, then the company PIN -->
		<Teleport to="#teleport-modal">
			<SmallModal
				v-if="showConfirmModal"
				class="tw-z-50"
				@close="showConfirmModal = !showConfirmModal"
			>
				<template v-slot:content>
					<div class="confirm-modal">
						<h3 class="confirm-title">{{ isSell ? "Confirm Sale" : "Confirm Investment" }}</h3>

						<div class="confirm-card">
							<div class="confirm-header">
								<div class="confirm-asset">
									<img
										v-if="tokenInfo.image"
										class="asset-image"
										:src="tokenInfo.image"
										:alt="tokenInfo.name"
									/>
									<div class="asset-info">
										<span class="asset-name">{{ tokenInfo.name }}</span>
										<span class="asset-shares">{{ quantityLabel(isSell ? sell.tokens : payload.tokens) }}</span>
									</div>
								</div>
							</div>

							<div v-if="isSell" class="confirm-details">
								<div class="detail-row">
									<span class="detail-label">Sale amount</span>
									<span class="detail-value">{{ formatAmountToDollar(sellGross, 2, currency) }}</span>
								</div>
								<div class="detail-row">
									<span class="detail-label">Transaction Fee ({{ sellFeePercent }}%)</span>
									<span class="detail-value">{{ formatAmountToDollar(sellFeeAmount, 2, currency) }}</span>
								</div>
								<div class="detail-divider"></div>
								<div class="detail-row total">
									<span class="detail-label">You receive</span>
									<span class="detail-value">{{ formatAmountToDollar(sellNet, 2, currency) }}</span>
								</div>
							</div>
							<div v-else class="confirm-details">
								<div class="detail-row">
									<span class="detail-label">Investment</span>
									<span class="detail-value">{{ formatAmountToDollar(investmentAmount, 2, currency) }}</span>
								</div>
								<div class="detail-row">
									<span class="detail-label">Transaction Fee ({{ buyFeePercent }}%)</span>
									<span class="detail-value">{{ formatAmountToDollar(buyFeeAmount, 2, currency) }}</span>
								</div>
								<div class="detail-divider"></div>
								<div class="detail-row total">
									<span class="detail-label">Total</span>
									<span class="detail-value">{{ formatAmountToDollar(totalPaymentAmount, 2, currency) }}</span>
								</div>
							</div>
						</div>

						<PinConfirmation
							class="pin-section"
							:btnTitle="isSell ? 'Confirm Sale' : 'Confirm Purchase'"
							@confirmed="submitOrder"
						/>
					</div>
				</template>
			</SmallModal>
		</Teleport>
	</div>
</template>

<script>
import PinConfirmation from "@/components/general/PinConfirmation.vue";
import SmallModal from "@/components/general/SmallModal.vue";
import TradeNumberInput from "@/components/wallet/TradeNumberInput.vue";
import { formatAmountToDollar } from "@/utils/helpers";
import { priceDecimals, unitWord, unitsLabel } from "@/utils/trade-view";
import { mapActions, mapGetters, mapState } from "vuex";

// A company pays half the deal's fee each way, at the real price (GE-Backend exchange-process
// getBuyTransactionFee / getSellTransactionFee, and exchange-purchase getTransactionFee, for an
// organisation that is not an API key).
const COMPANY_FEE_SHARE = 0.5;

export default {
	name: "ListingTokenAmountForm",

	emits: ["completed"],

	components: { TradeNumberInput, SmallModal, PinConfirmation },

	props: {
		// For a deal that trades at the market price (a metal, an NGX stock): turns on the Buy/Sell
		// tabs and words everything in what a token is. { kind: "commodity" | "ngx", name,
		// unit_label, unit_plural, unit_amount }. Absent, this is the plain Invest Now drawer.
		market: { type: Object, default: null },
	},

	data() {
		return {
			// Set while the form asks its inputs to validate themselves.
			amountCheck: false,
			buyValid: false,
			showConfirmModal: false,
			payload: { tokens: "", currencyAmount: "" },
			side: "Buy",
			// A sale, in the unit you think in (grams of a metal, shares) and in tokens.
			sell: { units: "", tokens: "" },
			sellValid: false,
		};
	},

	computed: {
		...mapState({
			tokenInfo: (state) => state.TokenModule.tokenInfo,
			storedTokenBalance: (state) => state.TokenModule.tokenBalance,
		}),
		...mapGetters("organisationModule", ["walletBalance"]),
		currency() {
			return this.tokenInfo.currency || "USD";
		},
		// The organisation's wallet in the deal's own currency.
		balance() {
			const wallet = (this.walletBalance || []).find((w) => w.currency === this.currency);
			return Number(wallet?.balance) || 0;
		},
		// The real price and the real minimum: what the backend prices and enforces.
		buyPrice() { return Number(this.tokenInfo.price?.buy) || 0; },
		minInvestment() { return Number(this.tokenInfo.min_trade?.buy) || 0; },
		buyFeePercent() { return (Number(this.tokenInfo.buy_fee) || 0) * COMPANY_FEE_SHARE; },
		sellFeePercent() { return (Number(this.tokenInfo.sell_fee) || 0) * COMPANY_FEE_SHARE; },
		investmentAmount() { return Number(this.payload.currencyAmount) || 0; },
		buyFeeAmount() { return (this.investmentAmount * this.buyFeePercent) / 100; },
		totalPaymentAmount() { return this.investmentAmount + this.buyFeeAmount; },

		unitSingular() { return unitWord(this.tokenInfo, 1); },
		isSell() { return Boolean(this.market) && this.side === "Sell"; },
		isMetal() { return this.market?.kind === "commodity"; },
		// How much metal one token is; 1 for everything else.
		unitAmount() { return this.isMetal ? Number(this.market.unit_amount) || 1 : 1; },
		// "Silver" on a metal deal, "Shares" on an NGX stock.
		subjectName() { return this.isMetal ? this.market.name || this.tokenInfo.name : "Shares"; },
		canSell() { return Number(this.storedTokenBalance) > 0; },
		// Priced per gram of a metal (when a token is a whole number of them), per share otherwise.
		perUnitText() {
			if (this.isMetal && this.market.unit_label) return `per ${this.market.unit_label}`;
			return `per ${this.unitSingular}`;
		},
		heldUnits() { return Number(this.storedTokenBalance || 0) * this.unitAmount; },
		heldLabel() { return this.quantityLabel(this.storedTokenBalance); },
		inputUnitLabel() {
			if (!this.isMetal || !this.market.unit_label) return "shares";
			return this.market.unit_plural || `${this.market.unit_label}s`;
		},
		sellQuestion() {
			return this.isMetal ? `How much ${this.subjectName.toLowerCase()} do you want to sell?` : "How many shares do you want to sell?";
		},
		submitLabel() {
			if (!this.market) return "Buy Shares";
			return `${this.isSell ? "Sell" : "Buy"} ${this.subjectName}`;
		},
		// A market sale fills at the last traded price.
		sellPrice() { return Number(this.tokenInfo.price?.exchange) || Number(this.tokenInfo.price?.buy) || 0; },
		sellGross() { return Number(this.sell.tokens || 0) * this.sellPrice; },
		sellFeeAmount() { return (this.sellGross * this.sellFeePercent) / 100; },
		sellNet() { return Math.max(this.sellGross - this.sellFeeAmount, 0); },
		buyPriceDecimals() { return priceDecimals(this.tokenInfo); },
	},

	methods: {
		...mapActions(["showToast"]),
		...mapActions("TokenModule", ["tradeToken"]),

		formatAmountToDollar,

		// Tokens in what they stand for: "19.93 grams", "40 shares".
		quantityLabel(tokens) {
			return unitsLabel(this.tokenInfo, Number(tokens) || 0, this.market ? { market: this.market } : {});
		},

		// A price per token as a price per gram, when a token is several grams.
		perUnit(price) {
			return (Number(price) || 0) / this.unitAmount;
		},

		setSide(side) {
			if (side === "Sell" && !this.canSell) return;
			this.side = side;
			this.amountCheck = false;
		},

		setCurrencyAmount(amount) {
			this.payload.currencyAmount = amount;
			this.payload.tokens = this.buyPrice > 0 ? (Number(amount) / this.buyPrice).toFixed(8) : "";
		},

		setSellUnits(units) {
			const value = Number(units) || 0;
			this.sell.units = String(units);
			this.sell.tokens = value > 0 ? (value / this.unitAmount).toFixed(8) : "";
		},

		currencyAmountValid(valid) {
			this.buyValid = valid;
		},

		sellUnitsValid(valid) {
			this.sellValid = valid;
		},

		validate() {
			this.amountCheck = false;
		},

		showModal() {
			this.amountCheck = true;
			setTimeout(() => {
				if (this.isSell ? !this.sellValid : !this.buyValid) return;
				this.showConfirmModal = true;
			}, 500);
		},

		submitOrder() {
			return this.isSell ? this.sellToken() : this.buyToken();
		},

		// A refusal (no deal-room access on a private deal, too little cash, a closed market)
		// comes back with the backend's reason, which the store shows as a toast.
		async sellToken() {
			try {
				// No price: a market order, filled at the best bid (the market maker's at least).
				const response = await this.tradeToken({ id: this.tokenInfo._id, amount: Number(this.sell.tokens), type: "sell" });
				if (!response?.data) return;
				this.showToast({ description: response.data.message, display: true, type: "success" });
				this.$emit("completed");
			} finally {
				this.showConfirmModal = false;
			}
		},

		async buyToken() {
			try {
				const response = await this.tradeToken({ id: this.tokenInfo._id, amount: Number(this.payload.tokens), type: "buy" });
				if (!response?.data) return;
				this.showToast({ description: response.data.message, display: true, type: "success" });
				this.$emit("completed");
			} finally {
				this.showConfirmModal = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// As investor-app's invest drawer.
$bg-primary: #F8FAFC;
$bg-card: #FFFFFF;
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #94A3B8;
$border-color: #E2E8F0;
$accent: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.listing-form {
	padding: 20px 0;
}

.side-tabs {
	display: flex;
	gap: 4px;
	padding: 4px;
	margin-bottom: 20px;
	border-radius: 8px;
	background: $bg-primary;
	border: 1px solid $border-color;
}

.side-tab {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	height: 42px;
	border: none;
	border-radius: 6px;
	background: transparent;
	color: $text-secondary;
	font-size: 14px;
	cursor: pointer;

	svg { width: 16px; height: 16px; }

	&.active {
		background: $bg-card;
		color: $text-primary;
		box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
	}

	&:disabled {
		color: $text-muted;
		cursor: not-allowed;
	}
}

.sell-all {
	align-self: flex-start;
	padding: 0;
	border: none;
	background: none;
	color: $accent;
	font-size: 13px;
	cursor: pointer;
}

// Balance Card
.balance-card {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px;
	background: linear-gradient(135deg, rgba($btn-primary, 0.03) 0%, rgba($accent, 0.05) 100%);
	border: 1px solid $border-color;
	border-radius: 10px;
	margin-bottom: 24px;
}

.balance-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	background: rgba($btn-primary, 0.08);
	border-radius: 8px;
	flex-shrink: 0;

	svg {
		width: 20px;
		height: 20px;
		color: $btn-primary;
	}
}

.balance-info {
	display: flex;
	flex-direction: column;
}

.balance-label {
	font-size: 12px;
	font-weight: 400;
	color: $text-muted;
}

.balance-value {
	font-size: 18px;
	font-weight: 400;
	color: $text-primary;
}

// Form
.invest-form {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.form-section {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.section-header {
	margin-bottom: 4px;
}

.section-title {
	font-size: 15px;
	font-weight: 400;
	color: $text-primary;
	margin: 0 0 4px;
}

.section-subtitle {
	font-size: 13px;
	font-weight: 400;
	color: $text-secondary;
	margin: 0;
}

.price-highlight {
	color: $btn-primary;
	font-weight: 500;
}

// Trade Info
.trade-info {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 12px;
	background: $bg-primary;
	border-radius: 8px;
	margin-top: 8px;
}

.info-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.info-label {
	font-size: 13px;
	font-weight: 400;
	color: $text-muted;
}

.info-value {
	font-size: 13px;
	font-weight: 400;
	color: $text-secondary;
}

// Summary Card
.summary-card {
	background: $bg-card;
	border: 1px solid $border-color;
	border-radius: 10px;
	padding: 16px;
}

.summary-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 8px 0;

	&.total {
		.summary-label,
		.summary-value {
			font-size: 15px;
			color: $text-primary;
		}
	}
}

.summary-label {
	font-size: 13px;
	font-weight: 400;
	color: $text-secondary;
}

.summary-value {
	font-size: 14px;
	font-weight: 400;
	color: $text-primary;
	text-align: right;
}

.summary-divider {
	height: 1px;
	background: $border-color;
	margin: 8px 0;
}

// Submit Button
.submit-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	padding: 14px 24px;
	background: $btn-primary;
	color: white;
	border: none;
	border-radius: 8px;
	font-size: 15px;
	font-weight: 400;
	cursor: pointer;
	transition: background 0.2s ease;

	svg {
		width: 18px;
		height: 18px;
	}

	&:hover {
		background: $btn-primary-hover;
	}
}

// Confirmation Modal
.confirm-modal {
	padding: 20px;
}

.confirm-title {
	font-size: 18px;
	font-weight: 400;
	color: $text-primary;
	text-align: center;
	margin: 0 0 20px;
}

.confirm-card {
	background: $bg-primary;
	border-radius: 10px;
	overflow: hidden;
}

.confirm-header {
	padding: 16px;
	border-bottom: 1px solid $border-color;
}

.confirm-asset {
	display: flex;
	align-items: center;
	gap: 12px;
}

.asset-image {
	width: 44px;
	height: 44px;
	border-radius: 8px;
	object-fit: cover;
}

.asset-info {
	display: flex;
	flex-direction: column;
}

.asset-name {
	font-size: 15px;
	font-weight: 400;
	color: $text-primary;
}

.asset-shares {
	font-size: 13px;
	font-weight: 400;
	color: $text-secondary;
}

.confirm-details {
	padding: 16px;
}

.detail-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 6px 0;

	&.total {
		.detail-label,
		.detail-value {
			font-size: 15px;
			color: $text-primary;
		}
	}
}

.detail-label {
	font-size: 13px;
	font-weight: 400;
	color: $text-secondary;
}

.detail-value {
	font-size: 14px;
	font-weight: 400;
	color: $text-primary;
}

.detail-divider {
	height: 1px;
	background: $border-color;
	margin: 8px 0;
}

.pin-section {
	margin-top: 16px;
}
</style>
